import React, { useState } from "react";
import axios from "axios";
import { notify } from "../../utils/toast";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

const url = `${process.env.REACT_APP_SERVER_BASE_URL}/form/submit`;

const PROVINCES = [
  "Punjab", "Sindh", "Khyber Pakhtunkhwa", "Balochistan",
  "Gilgit-Baltistan", "Azad Jammu & Kashmir", "Islamabad Capital Territory",
];

const STEPS = [
  { num: 1, label: "Personal Info",   icon: "👤" },
  { num: 2, label: "Location",        icon: "📍" },
  { num: 3, label: "About You",       icon: "✍️" },
];

function fmtCNIC(v) {
  const d = v.replace(/\D/g, "").slice(0, 13);
  let out = d.slice(0, 5);
  if (d.length > 5)  out += "-" + d.slice(5, 12);
  if (d.length > 12) out += "-" + d.slice(12, 13);
  return out;
}

function Field({ label, required, children, hint }) {
  return (
    <div className="jf-field">
      <label className="jf-label">
        {label}{required && <span className="jf-req">*</span>}
      </label>
      {children}
      {hint && <span className="jf-hint">{hint}</span>}
    </div>
  );
}

function Input({ ...props }) {
  return <input className="jf-input" {...props} />;
}

function Textarea({ ...props }) {
  return <textarea className="jf-input jf-textarea" {...props} />;
}

function Select({ children, ...props }) {
  return (
    <select className="jf-input jf-select" {...props}>
      {children}
    </select>
  );
}

export default function ContactForm({ handleCloseForm }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Step 1
  const [name, setName]       = useState("");
  const [cnic, setCnic]       = useState("");
  const [phone, setPhone]     = useState("");
  const [whatsapp, setWhatsapp] = useState("");

  // Step 2
  const [province, setProvince] = useState("");
  const [city, setCity]         = useState("");
  const [area, setArea]         = useState("");
  const [address, setAddress]   = useState("");

  // Step 3
  const [whyJoin, setWhyJoin]           = useState("");
  const [occupation, setOccupation]     = useState("");
  const [contribution, setContribution] = useState("");

  const step1Valid = name.trim().length > 2 && cnic.replace(/\D/g,"").length === 13 && phone && whatsapp;
  const step2Valid = province && city.trim().length > 0 && address.trim().length > 5;
  const step3Valid = whyJoin.trim().length > 10;

  const canNext = step === 1 ? step1Valid : step === 2 ? step2Valid : step3Valid;

  const handleNext = () => { if (canNext) setStep(s => s + 1); };
  const handleBack = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    if (!step3Valid) return;
    setLoading(true);
    try {
      const res = await axios.post(url, {
        name, cnic, phone, whatsappNumber: whatsapp,
        province, city, area, address,
        whyJoin, occupation, contribution,
      });
      if (res?.data?.success) {
        setDone(true);
      }
    } catch (err) {
      notify.error(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="jf-success">
        <div className="jf-success__icon">🎉</div>
        <h3 className="jf-success__title">Application Received!</h3>
        <p className="jf-success__body">
          Thank you, <strong>{name}</strong>. Your application to join Technology Movement
          Pakistan has been submitted. Our team will review your details and contact you
          on your WhatsApp number shortly.
        </p>
        <button className="jf-btn jf-btn--primary" onClick={handleCloseForm}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="jf-root">
      {/* Header */}
      <div className="jf-hero">
        <div className="jf-hero__badge">JOIN THE MOVEMENT</div>
        <h2 className="jf-hero__title">Become a Member</h2>
        <p className="jf-hero__sub">
          Technology Movement Pakistan — building a digital future for every citizen.
        </p>
      </div>

      {/* Step indicators */}
      <div className="jf-steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.num}>
            <div className={`jf-step ${step === s.num ? "jf-step--active" : ""} ${step > s.num ? "jf-step--done" : ""}`}>
              <div className="jf-step__circle">
                {step > s.num ? "✓" : s.icon}
              </div>
              <span className="jf-step__label">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`jf-step__line ${step > s.num ? "jf-step__line--done" : ""}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Form body */}
      <div className="jf-body">

        {step === 1 && (
          <div className="jf-panel">
            <h4 className="jf-panel__title">Personal Information</h4>
            <div className="jf-grid">
              <Field label="Full Name" required>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Ahmed Khan" />
              </Field>
              <Field label="CNIC Number" required hint="Format: 42201-1234567-1">
                <Input
                  value={cnic}
                  onChange={e => setCnic(fmtCNIC(e.target.value))}
                  placeholder="XXXXX-XXXXXXX-X"
                  maxLength={15}
                />
              </Field>
              <Field label="Phone Number" required>
                <PhoneInput
                  className="jf-phone"
                  international
                  defaultCountry="PK"
                  countryCallingCodeEditable={false}
                  value={phone}
                  onChange={setPhone}
                  placeholder="03XX XXXXXXX"
                />
              </Field>
              <Field label="WhatsApp Number" required>
                <PhoneInput
                  className="jf-phone"
                  international
                  defaultCountry="PK"
                  countryCallingCodeEditable={false}
                  value={whatsapp}
                  onChange={setWhatsapp}
                  placeholder="WhatsApp Number"
                />
              </Field>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="jf-panel">
            <h4 className="jf-panel__title">Location Details</h4>
            <div className="jf-grid">
              <Field label="Province" required>
                <Select value={province} onChange={e => setProvince(e.target.value)}>
                  <option value="">Select province…</option>
                  {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                </Select>
              </Field>
              <Field label="City" required>
                <Input value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Karachi" />
              </Field>
              <Field label="Area / Neighbourhood">
                <Input value={area} onChange={e => setArea(e.target.value)} placeholder="e.g. DHA Phase 5" />
              </Field>
              <Field label="Complete Address" required>
                <Textarea
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder="House No, Street, Block…"
                  rows={3}
                />
              </Field>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="jf-panel">
            <h4 className="jf-panel__title">About You</h4>
            <div className="jf-grid">
              <Field label="Why do you want to join TMP?" required hint="Minimum 10 characters">
                <Textarea
                  value={whyJoin}
                  onChange={e => setWhyJoin(e.target.value)}
                  placeholder="Share your motivation for joining the movement…"
                  rows={4}
                />
              </Field>
              <Field label="Occupation / Profession">
                <Input value={occupation} onChange={e => setOccupation(e.target.value)} placeholder="e.g. Software Engineer (optional)" />
              </Field>
              <Field label="How can you contribute to the party?">
                <Textarea
                  value={contribution}
                  onChange={e => setContribution(e.target.value)}
                  placeholder="e.g. Volunteering, social media outreach, technical skills… (optional)"
                  rows={3}
                />
              </Field>
            </div>
          </div>
        )}

      </div>

      {/* Footer nav */}
      <div className="jf-footer">
        {step > 1
          ? <button className="jf-btn jf-btn--ghost" onClick={handleBack}>← Back</button>
          : <div />
        }
        <div className="jf-footer__right">
          <span className="jf-progress-txt">{step} / {STEPS.length}</span>
          {step < 3
            ? (
              <button
                className={`jf-btn jf-btn--primary ${!canNext ? "jf-btn--disabled" : ""}`}
                onClick={handleNext}
                disabled={!canNext}
              >
                Next →
              </button>
            )
            : (
              <button
                className={`jf-btn jf-btn--primary ${(!canNext || loading) ? "jf-btn--disabled" : ""}`}
                onClick={handleSubmit}
                disabled={!canNext || loading}
              >
                {loading ? "Submitting…" : "Submit Application"}
              </button>
            )
          }
        </div>
      </div>
    </div>
  );
}
